import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { useFormLayout } from '../../../contexts/FormLayoutContext';
import { useFieldMetadata } from '../../../contexts/FieldMetadataContext';
import RowComponent from './RowComponent';
import SubmitButtonComponent from './SubmitButtonComponent';
import DropZone, { EMPTY_DROPZONE } from './DropZone';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * FormLayoutUI
 * -----------------------------------------------------------------------------
 * Renders the rows that make up the builder canvas.  
 * – Shows a “Drag fields here…” drop-zone when there are **zero** non-submit rows.  
 * – Never falls back to the spinner just because `fieldsMetadata` is empty
 *   (that situation now happens legitimately when you’ve deleted the last field).
 */
const FormLayoutUI = () => {
  const {
    layoutConfig,
    handleDrop,
    showDropZones,
    setShowDropZones,
    handleDragStart,
    handleDragEnd,
    isLoading: layoutLoading            
  } = useFormLayout();

  const { fieldsMetadata, isLoading: metadataLoading } = useFieldMetadata();

  /*───────────────────────────────────────────────────────────────────────────*/
  /* Derive rowKeys synchronously (prevents one‑frame flicker)                */
  /* – Keeps ALL original steps: filter, sort, de‑dupe, non‑empty rows, logs  */
  /*───────────────────────────────────────────────────────────────────────────*/
  const rowKeys = useMemo(() => {
    // Skip while the layout itself is still loading to avoid the transient
    // “zero rows” state that caused the flicker.
    if (layoutLoading) return [];

    // 1️⃣  Bail out if layoutConfig is completely empty / undefined
    if (!layoutConfig || Object.keys(layoutConfig).length === 0) {
      return [];
    }

    // 2️⃣  Collect keys except submitRow
    const filteredRowKeys = Object.keys(layoutConfig).filter(
      (key) => key !== 'submitRow'
    );

    // 3️⃣  Sort numerically (row0, row1, …)
    const sortedRowKeys = filteredRowKeys.sort((a, b) => {
      const indexA = parseInt(a.replace('row', ''), 10);
      const indexB = parseInt(b.replace('row', ''), 10);
      if (isNaN(indexA) || isNaN(indexB)) {
        console.warn('[FormLayoutUI] ⚠️ Invalid row key detected:', { a, b });
        return 0;
      }
      return indexA - indexB;
    });

    // 4️⃣  De‑dupe (edge‑case safety)
    const uniqueSorted = [...new Set(sortedRowKeys)];

    // 5️⃣  **NEW** – keep only rows that actually contain ≥1 field
    const nonEmptyRows = uniqueSorted.filter(
      (rowKey) =>
        layoutConfig[rowKey] !== undefined && layoutConfig[rowKey].length > 0
    );

    // (No setTimeout needed – value is derived, not setState)
    return nonEmptyRows;
  }, [layoutConfig, layoutLoading]);

  /*───────────────────────────────────────────────────────────────────────────*/
  /* Loading guard                                                           */
  /*                                                                         */
  /* Show the spinner only if BOTH:                                          */
  /*   – metadata is still loading, **and**                                  */
  /*   – there is at least one real field row that needs that metadata.      */
  /*                                                                         */
  /* When you delete the last field, `metadataLoading` stays true (the        */
  /* context waits for data that no longer exists), but there are zero        */
  /* non-submit rows—so we skip the spinner and render the empty drop zone.   */
  /*───────────────────────────────────────────────────────────────────────────*/
  const hasContentRows = Object.keys(layoutConfig || {}).some(
    (k) => k.startsWith('row') && (layoutConfig[k]?.length ?? 0) > 0
  );

  /* EARLY EXIT: wait until FormLayoutContext finishes loading the layout */
  if (layoutLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress size={40} aria-label="Loading form layout" />
      </Box>
    );
  }

  if (Object.keys(layoutConfig || {}).length === 0 ||
      (metadataLoading && hasContentRows)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress size={40} aria-label="Loading form layout" />
      </Box>
    );
  }



  return (
    <>
      {rowKeys.length === 0 ? (
        /* Empty-canvas drop-zone */
        <DropZone
          type={EMPTY_DROPZONE}
          onDrop={handleDrop}
          alwaysVisible
        />
      ) : (
        /* Non-empty rows */
        rowKeys.map((rowKey, index) => {
          const row = layoutConfig[rowKey];

          if (!row || row.length === 0) {
            // Shouldn’t happen now but double-guard anyway
            console.warn(
              `[FormLayoutUI] Skipping empty or undefined row ${rowKey}.`
            );
            return null;
          }

          // IMPORTANT:
          // `index` here is just the array index of `rowKeys` (after filtering),
          // and it can diverge from the actual numeric row in layoutConfig.
          // We must derive the numeric row index from the rowKey itself
          // (row0, row1, row2, ...) so drops target the correct row.
          const numericRowIndex = parseInt(rowKey.replace('row', ''), 10);

          if (Number.isNaN(numericRowIndex)) {
            console.warn('[FormLayoutUI] ⚠️ Could not parse numeric rowIndex from rowKey:', {
              rowKey,
              index,
            });
          }

          return (
            <RowComponent
              key={rowKey}
              rowKey={rowKey}
              rowIndex={numericRowIndex}
              row={row}
              layoutConfig={layoutConfig}
              moveItem={handleDrop}
              fieldsMetadata={fieldsMetadata}
              mode="builder"
              showDropZones={showDropZones}
              setShowDropZones={setShowDropZones}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
            />
          );
        })

      )}

      {/* Submit button row (always rendered last) */}
      {layoutConfig.submitRow && (
        <SubmitButtonComponent field={layoutConfig.submitRow[0]} />
      )}
    </>
  );
};

export default FormLayoutUI;
